import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicator from '@mui/icons-material/DragIndicator';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import {
  DEFAULT_BRAND_COLORS,
  LAYOUT_CARDS,
  normalizeHex,
} from './layoutCards';
import { ORDER_SUMMARY_COLUMNS } from './orderSummary';

const COLOR_FIELDS = [
  { key: 'header', label: 'Header bar' },
  { key: 'button', label: 'Buttons' },
  { key: 'text', label: 'Body text' },
  { key: 'card', label: 'Card fill' },
];

const listedSummaryKeys = (columns) =>
  (Array.isArray(columns) ? columns : []).filter((key) =>
    ORDER_SUMMARY_COLUMNS.some((col) => col.key === key)
  );

const reorderKeys = (keys, fromKey, toIndex) => {
  const next = keys.slice();
  const from = next.indexOf(fromKey);
  if (from < 0 || toIndex < 0 || toIndex >= next.length || from === toIndex) {
    return keys;
  }
  next.splice(from, 1);
  next.splice(toIndex, 0, fromKey);
  return next;
};

const captureRowTops = (listEl) => {
  const tops = {};
  if (!listEl) return tops;
  const rows = listEl.querySelectorAll('.mcc-summary-col');
  for (let i = 0; i < rows.length; i++) {
    const key = rows[i].getAttribute('data-col-key');
    if (key) {
      tops[key] = rows[i].getBoundingClientRect().top;
    }
  }
  return tops;
};

const animateRowFlip = (listEl, previousTops) => {
  if (!listEl || !previousTops) return;
  const rows = listEl.querySelectorAll('.mcc-summary-col');
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const key = row.getAttribute('data-col-key');
    const prev = previousTops[key];
    if (prev == null) continue;
    const next = row.getBoundingClientRect().top;
    const dy = prev - next;
    if (Math.abs(dy) < 1) continue;
    row.style.transition = 'none';
    row.style.transform = `translateY(${dy}px)`;
    row.getBoundingClientRect();
    row.style.transition = 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)';
    row.style.transform = '';
  }
};

const createRowGhost = (row, rect) => {
  const ghost = row.cloneNode(true);
  ghost.className = `${row.className} mcc-summary-col-ghost`;
  ghost.removeAttribute('data-col-key');
  ghost.style.position = 'fixed';
  ghost.style.left = `${rect.left}px`;
  ghost.style.top = `${rect.top}px`;
  ghost.style.width = `${rect.width}px`;
  ghost.style.zIndex = '4000';
  ghost.style.margin = '0';
  ghost.style.pointerEvents = 'none';
  ghost.style.boxSizing = 'border-box';
  ghost.style.opacity = '1';
  document.body.appendChild(ghost);
  return ghost;
};

const EmailLayoutTools = ({
  t,
  colors = DEFAULT_BRAND_COLORS,
  onChangeColor,
  onAddCard,
  hideAmountDue,
  onToggleHideAmount,
  hasOrderSummary,
  summaryConfig,
  onChangeSummaryConfig,
  paymentEnabled = true,
}) => {
  const [draggingKey, setDraggingKey] = useState('');
  const [previewKeys, setPreviewKeys] = useState(null);
  const [flashKey, setFlashKey] = useState('');
  const listRef = useRef(null);
  const posRef = useRef({});
  const dragRef = useRef(null);
  const previewRef = useRef(null);
  const columnsRef = useRef([]);
  const maskRef = useRef({});
  const updateRef = useRef(null);
  const flashTimer = useRef(null);

  const translate = (key, fallback) =>
    t ? t(key, { defaultValue: fallback }) : fallback;
  const summaryColumns = paymentEnabled
    ? ORDER_SUMMARY_COLUMNS
    : ORDER_SUMMARY_COLUMNS.filter((col) => col.type !== 'money');
  const paletteCards = paymentEnabled
    ? LAYOUT_CARDS
    : LAYOUT_CARDS.filter((card) => card.id !== 'payment' && card.id !== 'cta');
  const columns = (summaryConfig && summaryConfig.columns) || [];
  const mask = (summaryConfig && summaryConfig.mask) || {};
  const listedKeys = listedSummaryKeys(columns).filter(
    (key) =>
      paymentEnabled || (key !== 'price' && key !== 'previous_price')
  );
  const displayKeys = previewKeys || listedKeys;
  const addable = summaryColumns.filter(
    (col) => displayKeys.indexOf(col.key) === -1
  );
  const canDelete = displayKeys.length > 1;

  columnsRef.current = listedKeys;
  maskRef.current = mask;

  const updateSummary = (nextColumns, nextMask) => {
    if (onChangeSummaryConfig) {
      onChangeSummaryConfig({
        columns: nextColumns,
        mask: nextMask || mask,
      });
    }
  };
  updateRef.current = (nextColumns) => {
    updateSummary(nextColumns, maskRef.current);
  };

  const pulseKey = (key) => {
    if (flashTimer.current) {
      window.clearTimeout(flashTimer.current);
    }
    setFlashKey(key);
    flashTimer.current = window.setTimeout(() => {
      setFlashKey('');
      flashTimer.current = null;
    }, 520);
  };

  const toggleColumn = (key, show) => {
    if (draggingKey) return;
    if (show) {
      if (columns.indexOf(key) !== -1) return;
      updateSummary(columns.concat([key]), mask);
      return;
    }
    if (columns.length <= 1) return;
    const nextMask = {};
    Object.keys(mask).forEach((maskKey) => {
      if (maskKey !== key) {
        nextMask[maskKey] = mask[maskKey];
      }
    });
    updateSummary(
      columns.filter((colKey) => colKey !== key),
      nextMask
    );
  };

  const changeMask = (key, value) => {
    const nextMask = {};
    Object.keys(mask).forEach((maskKey) => {
      nextMask[maskKey] = mask[maskKey];
    });
    const count = parseInt(value, 10);
    if (!Number.isFinite(count) || count < 1) {
      delete nextMask[key];
    } else {
      nextMask[key] = Math.min(32, count);
    }
    updateSummary(columns, nextMask);
  };

  const removeColumn = (key) => {
    toggleColumn(key, false);
  };

  const applyOrder = (nextKeys, movedKey) => {
    if (!nextKeys || nextKeys.join(',') === listedKeys.join(',')) {
      return;
    }
    setPreviewKeys(nextKeys);
    previewRef.current = nextKeys;
    updateSummary(nextKeys, mask);
    if (movedKey) {
      pulseKey(movedKey);
    }
  };

  const moveColumnBy = (key, delta) => {
    if (draggingKey) return;
    const from = displayKeys.indexOf(key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= displayKeys.length) {
      return;
    }
    applyOrder(reorderKeys(displayKeys, key, to), key);
  };

  useLayoutEffect(() => {
    animateRowFlip(listRef.current, posRef.current);
    posRef.current = captureRowTops(listRef.current);
  }, [displayKeys.join(',')]);

  useEffect(() => {
    if (draggingKey) return;
    if (!previewKeys) return;
    if (previewKeys.join(',') === listedKeys.join(',')) {
      setPreviewKeys(null);
      previewRef.current = null;
    }
  }, [draggingKey, previewKeys, listedKeys]);

  useEffect(
    () => () => {
      if (flashTimer.current) {
        window.clearTimeout(flashTimer.current);
      }
      const drag = dragRef.current;
      if (drag) {
        if (drag.move) {
          window.removeEventListener('pointermove', drag.move);
        }
        if (drag.up) {
          window.removeEventListener('pointerup', drag.up);
          window.removeEventListener('pointercancel', drag.up);
        }
        if (drag.ghost && drag.ghost.parentNode) {
          drag.ghost.parentNode.removeChild(drag.ghost);
        }
      }
      document.body.classList.remove('mcc-summary-sorting');
    },
    []
  );

  const startRowDrag = (event, key) => {
    if (event.button != null && event.button !== 0) return;
    const row = event.currentTarget.closest
      ? event.currentTarget.closest('.mcc-summary-col')
      : null;
    const list = listRef.current;
    if (!row || !list) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = row.getBoundingClientRect();
    const ghost = createRowGhost(row, rect);
    const startKeys = displayKeys.slice();
    previewRef.current = startKeys;
    setPreviewKeys(startKeys);
    setDraggingKey(key);
    document.body.classList.add('mcc-summary-sorting');

    const drag = {
      key,
      ghost,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };

    const move = (moveEvent) => {
      moveEvent.preventDefault();
      if (!drag.ghost) return;
      drag.ghost.style.left = `${moveEvent.clientX - drag.offsetX}px`;
      drag.ghost.style.top = `${moveEvent.clientY - drag.offsetY}px`;
      const rows = list.querySelectorAll('.mcc-summary-col');
      let nextIndex = rows.length - 1;
      for (let i = 0; i < rows.length; i++) {
        const box = rows[i].getBoundingClientRect();
        if (moveEvent.clientY < box.top + box.height / 2) {
          nextIndex = i;
          break;
        }
      }
      const current = previewRef.current || startKeys;
      const next = reorderKeys(current, drag.key, nextIndex);
      if (next.join(',') === current.join(',')) {
        return;
      }
      previewRef.current = next;
      setPreviewKeys(next);
    };

    const up = () => {
      window.removeEventListener('pointermove', drag.move);
      window.removeEventListener('pointerup', drag.up);
      window.removeEventListener('pointercancel', drag.up);
      document.body.classList.remove('mcc-summary-sorting');
      const placeholder = list.querySelector(
        `[data-col-key="${drag.key}"]`
      );
      const target = placeholder ? placeholder.getBoundingClientRect() : null;
      const finish = () => {
        if (drag.ghost && drag.ghost.parentNode) {
          drag.ghost.parentNode.removeChild(drag.ghost);
        }
        dragRef.current = null;
        setDraggingKey('');
        const nextKeys = previewRef.current || startKeys;
        if (nextKeys.join(',') !== columnsRef.current.join(',')) {
          if (updateRef.current) {
            updateRef.current(nextKeys);
          }
          pulseKey(drag.key);
        } else {
          setPreviewKeys(null);
          previewRef.current = null;
        }
      };
      if (drag.ghost && target) {
        drag.ghost.style.transition =
          'left 180ms cubic-bezier(0.2, 0.8, 0.2, 1), top 180ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 180ms ease, box-shadow 180ms ease';
        drag.ghost.style.left = `${target.left}px`;
        drag.ghost.style.top = `${target.top}px`;
        drag.ghost.style.transform = 'scale(1)';
        drag.ghost.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.08)';
        window.setTimeout(finish, 190);
      } else {
        finish();
      }
    };

    drag.move = move;
    drag.up = up;
    dragRef.current = drag;
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  return (
    <div className="mcc-layout-tools">
      <div className="mcc-brand-colors">
        <h3>
          {translate('MessageControl.BrandColors', 'Branding colors')}
        </h3>
        <p className="mcc-helper">
          {translate(
            'MessageControl.BrandColorsHelp',
            'These colors apply to header, buttons, and cards in this email. They are saved with the template.'
          )}
        </p>
        <div className="mcc-color-grid">
          {COLOR_FIELDS.map((field) => (
            <label className="mcc-color-field" key={field.key}>
              <span>
                {translate(`MessageControl.Color.${field.key}`, field.label)}
              </span>
              <span className="mcc-color-controls">
                <input
                  type="color"
                  aria-label={field.label}
                  value={normalizeHex(
                    colors[field.key],
                    DEFAULT_BRAND_COLORS[field.key]
                  )}
                  onChange={(event) =>
                    onChangeColor(field.key, event.target.value)
                  }
                />
                <input
                  type="text"
                  className="mcc-color-hex"
                  maxLength={7}
                  value={colors[field.key] || ''}
                  onChange={(event) =>
                    onChangeColor(field.key, event.target.value)
                  }
                />
              </span>
            </label>
          ))}
        </div>
      </div>
      {hasOrderSummary ? (
        <div className="mcc-summary-props">
          <h3>
            {translate(
              'MessageControl.OrderSummaryProps',
              'Order summary properties'
            )}
          </h3>
          <p className="mcc-helper">
            {translate(
              'MessageControl.OrderSummaryPropsHelp',
              'Show or hide any column, change the order, and mask values. Drag a row or use the arrows. Add extra prescription fields to the table.'
            )}
          </p>
          <div
            className={`mcc-summary-cols${
              draggingKey ? ' is-sorting' : ''
            }`}
            ref={listRef}
          >
            {displayKeys.map((key) => {
              const col = summaryColumns.filter(
                (item) => item.key === key
              )[0];
              if (!col) return null;
              const checked = displayKeys.indexOf(key) !== -1;
              const deleteLabel = translate(
                'MessageControl.OrderSummaryRemove',
                'Remove column'
              );
              const moveUpLabel = translate(
                'MessageControl.OrderSummaryMoveUp',
                'Move up'
              );
              const moveDownLabel = translate(
                'MessageControl.OrderSummaryMoveDown',
                'Move down'
              );
              const dragLabel = translate(
                'MessageControl.OrderSummaryDrag',
                'Drag to reorder'
              );
              const index = displayKeys.indexOf(key);
              const classNames = ['mcc-summary-col'];
              if (draggingKey === key) {
                classNames.push('is-dragging');
              }
              if (flashKey === key) {
                classNames.push('is-dropped');
              }
              return (
                <div
                  className={classNames.join(' ')}
                  data-col-key={key}
                  key={key}
                >
                  <div className="mcc-summary-col-main">
                    <div className="mcc-summary-order">
                      <span
                        className="mcc-summary-drag"
                        title={dragLabel}
                        aria-label={dragLabel}
                        onPointerDown={(event) => startRowDrag(event, key)}
                      >
                        <DragIndicator fontSize="small" />
                      </span>
                      <span className="mcc-summary-order-arrows">
                        <IconButton
                          aria-label={moveUpLabel}
                          title={moveUpLabel}
                          size="small"
                          disabled={!!draggingKey || index <= 0}
                          onClick={() => moveColumnBy(key, -1)}
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label={moveDownLabel}
                          title={moveDownLabel}
                          size="small"
                          disabled={
                            !!draggingKey || index >= displayKeys.length - 1
                          }
                          onClick={() => moveColumnBy(key, 1)}
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </span>
                    </div>
                    <label className="mcc-hide-amount">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          event.stopPropagation();
                          toggleColumn(key, event.target.checked);
                        }}
                      />
                      <span>
                        {translate(
                          `MessageControl.OrderSummaryCol.${key}`,
                          col.label
                        )}
                      </span>
                    </label>
                  </div>
                  <div className="mcc-summary-mask-box">
                    <span className="mcc-summary-mask-label">
                      {translate(
                        'MessageControl.OrderSummaryMask',
                        'Show first'
                      )}
                    </span>
                    <input
                      type="number"
                      className="mcc-summary-mask-count"
                      min={0}
                      max={32}
                      disabled={!checked}
                      aria-label={translate(
                        'MessageControl.OrderSummaryMask',
                        'Show first'
                      )}
                      value={mask[key] || 0}
                      onChange={(event) =>
                        changeMask(key, event.target.value)
                      }
                    />
                    <span className="mcc-summary-mask-label">
                      {translate(
                        'MessageControl.OrderSummaryMaskChars',
                        'chars'
                      )}
                    </span>
                    <IconButton
                      aria-label={deleteLabel}
                      title={deleteLabel}
                      size="small"
                      disabled={!canDelete || !checked || !!draggingKey}
                      onClick={() => removeColumn(key)}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </div>
          {addable.length ? (
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              className="mcc-summary-add"
            >
              <InputLabel id="mcc-summary-add-label" shrink>
                {translate(
                  'MessageControl.OrderSummaryAdd',
                  'Add a prescription column'
                )}
              </InputLabel>
              <Select
                labelId="mcc-summary-add-label"
                id="mcc-summary-add"
                value=""
                displayEmpty
                notched
                label={translate(
                  'MessageControl.OrderSummaryAdd',
                  'Add a prescription column'
                )}
                renderValue={() =>
                  translate(
                    'MessageControl.OrderSummaryAddPlaceholder',
                    'Choose a column'
                  )
                }
                onChange={(event) => {
                  const key = event.target.value;
                  if (key) {
                    toggleColumn(key, true);
                  }
                }}
              >
                {addable.map((col) => (
                  <MenuItem key={col.key} value={col.key}>
                    {translate(
                      `MessageControl.OrderSummaryCol.${col.key}`,
                      col.label
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </div>
      ) : paymentEnabled ? (
        <label className="mcc-hide-amount">
          <input
            type="checkbox"
            checked={!!hideAmountDue}
            onChange={(event) => {
              event.stopPropagation();
              if (onToggleHideAmount) {
                onToggleHideAmount(event.target.checked);
              }
            }}
          />
          <span>
            {translate(
              'MessageControl.HideAmountDue',
              'Hide amount due on the order summary'
            )}
          </span>
        </label>
      ) : null}
      <div className="mcc-card-palette">
        <h3>
          {translate('MessageControl.LayoutCards', 'Add a card')}
        </h3>
        <p className="mcc-helper">
          {translate(
            'MessageControl.LayoutCardsHelp',
            'Drag a block onto the canvas, or click to insert it. Grab the :: handle on any card, Pay Now, or Total Due to drag it anywhere on the canvas — same motion as the prescription columns. Drop on the left or right of a row to place two blocks side by side. The navy footer stays inside the same email card. Only Powered by stays locked.'
          )}
        </p>
        <div className="mcc-card-palette-list">
          {paletteCards.map((card) => (
            <button
              type="button"
              key={card.id}
              className="mcc-card-chip"
              draggable
              title={card.hint}
              onDragStart={(event) => {
                event.dataTransfer.setData('text/mcc-card', card.id);
                event.dataTransfer.setData(
                  'text/plain',
                  `mcc-card:${card.id}`
                );
                event.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => onAddCard(card.id)}
            >
              <strong>{card.label}</strong>
              <span>{card.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailLayoutTools;



// WEBPACK FOOTER //
// ./src/components/message-control-center/EmailLayoutTools.js